import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Volume2, VolumeX } from 'lucide-react';

// Full-screen video modal shared by the welcome-video overlay and the
// per-page "Ver tutorial" buttons. Autoplay starts muted (browsers block
// unmuted autoplay outright); unmuting requires a direct click, which is a
// real user gesture and is allowed.
const VideoModal = ({ src, onClose, caption }) => {
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const toggleSound = () => setMuted((m) => !m);

  // Portaled to <body> so `fixed inset-0` always centers on the real
  // viewport — an ancestor with backdrop-blur/filter (e.g. the page's
  // <nav>) would otherwise become the containing block for this modal.
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8">
      <div className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl border border-border bg-black">
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          <button
            onClick={toggleSound}
            data-testid="video-modal-toggle-sound"
            className="bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <button
            onClick={onClose}
            data-testid="video-modal-close"
            className="bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {muted && (
          <button
            onClick={toggleSound}
            data-testid="video-modal-unmute-prompt"
            className="absolute top-3 left-3 z-10 flex items-center gap-2 bg-black/60 hover:bg-black/80 text-white text-sm font-medium rounded-full pl-3 pr-4 py-2 transition-colors"
          >
            <VolumeX className="w-4 h-4" />
            Activar sonido
          </button>
        )}

        <video
          src={src}
          className="w-full h-full"
          autoPlay
          muted={muted}
          controls
          playsInline
          onEnded={onClose}
        />

        {!muted && caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none">
            <p className="text-white text-sm sm:text-base font-medium">{caption}</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default VideoModal;
